import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { Brand, cn } from "../../ui";
import type { BrandIdentity } from "../../ui/Brand";
import type { SiteLink, SiteNavItem } from "./siteConfig";

/* ----------------------------------------------------------- SmartLink */

/** Router link for app paths, plain anchor for hashes, tel/mailto and external URLs */
export const SmartLink: React.FC<
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { href: string; external?: boolean }
> = ({ href, external, children, ...props }) => {
  // Fragment links ("/affiliate#renovate") use a plain anchor so the browser scrolls to the target
  const isAppPath = href.startsWith("/") && !href.includes("#") && !external;
  if (isAppPath) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      {...props}
    >
      {children}
    </a>
  );
};

/* ------------------------------------------------------------ Dropdown */

const NavDropdown: React.FC<{ item: SiteNavItem; active: boolean }> = ({ item, active }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const id = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      // Hover-to-open for mice only, so a tap on touch screens doesn't open then immediately toggle closed
      onPointerEnter={(e) => e.pointerType === "mouse" && setOpen(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex h-9 items-center gap-1 rounded-lg px-3 text-sm font-medium transition-colors",
          active || open ? "text-gray-900" : "text-gray-600 hover:text-gray-900"
        )}
      >
        {item.label}
        <ChevronDown className={cn("size-3.5 text-gray-400 transition-transform", open && "rotate-180")} />
      </button>
      {/* pt-2 bridges the hover gap between trigger and panel */}
      <div
        id={id}
        className={cn(
          "absolute left-0 top-full z-50 pt-2 transition duration-150",
          open ? "visible translate-y-0 opacity-100" : "invisible -translate-y-1 opacity-0"
        )}
      >
        <DropdownPanel links={item.children ?? []} onNavigate={() => setOpen(false)} />
      </div>
    </div>
  );
};

const DropdownPanel: React.FC<{ links: SiteLink[]; onNavigate: () => void; className?: string }> = ({
  links,
  onNavigate,
  className,
}) => (
  <div className={cn("w-72 rounded-xl bg-white p-1.5 shadow-overlay ring-1 ring-gray-950/5", className)}>
    {links.map((link) => (
      <SmartLink
        key={link.href + link.label}
        href={link.href}
        external={link.external}
        onClick={onNavigate}
        className="group flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50"
      >
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900">{link.label}</span>
          {link.description && <span className="mt-0.5 block text-meta text-gray-500">{link.description}</span>}
        </span>
        {link.external && (
          <ArrowUpRight className="mt-0.5 size-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
        )}
      </SmartLink>
    ))}
  </div>
);

/** Right-aligned button that opens a list of links, e.g. "Log in" to each FlipIQ product */
export const MenuButton: React.FC<{
  label: React.ReactNode;
  links: SiteLink[];
  heading?: string;
  variant?: "brand" | "secondary";
}> = ({ label, links, heading, variant = "secondary" }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const id = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          variant === "brand"
            ? "bg-brand-600 text-white hover:bg-brand-700 focus-visible:ring-brand-500"
            : "bg-white text-gray-800 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-gray-400"
        )}
      >
        {label}
        <ChevronDown className={cn("size-3.5 opacity-70 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div id={id} className="absolute right-0 top-full z-50 animate-fade-in pt-2">
          <div className="w-72 rounded-xl bg-white shadow-overlay ring-1 ring-gray-950/5">
            {heading && (
              <p className="border-b border-gray-100 px-4 py-2.5 text-meta font-medium text-gray-500">
                {heading}
              </p>
            )}
            <DropdownPanel links={links} onNavigate={() => setOpen(false)} className="w-full shadow-none ring-0" />
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------- Header */

export interface SiteHeaderProps {
  nav: SiteNavItem[];
  /** Right side on desktop (buttons) */
  actions?: React.ReactNode;
  /** Bottom of the mobile drawer; defaults to `actions` */
  mobileActions?: React.ReactNode;
  /** Small utility element shown left of actions on xl screens (phone number) */
  utility?: React.ReactNode;
  brandTo?: string;
  /** PRD 11.2: an agent's own name and logo instead of the Marketplace lockup */
  brandIdentity?: BrandIdentity;
  /** Extra groups shown only in the mobile drawer (e.g. product logins) */
  mobileExtraNav?: SiteNavItem[];
  className?: string;
}

/** Slim sticky marketing navigation shared by every public page */
export const SiteHeader: React.FC<SiteHeaderProps> = ({
  nav,
  actions,
  mobileActions,
  utility,
  brandTo = "/",
  brandIdentity,
  mobileExtraNav = [],
  className,
}) => {
  const { pathname, hash } = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => setMobileOpen(false), [pathname, hash]);

  const isActive = (item: SiteNavItem) => {
    const prefixes = item.match ?? [item.href, ...(item.children ?? []).map((c) => c.href)];
    return prefixes.some((p) => !!p && p.startsWith("/") && p !== "/" && pathname.startsWith(p));
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-40 border-b border-gray-200/70 bg-white/85 backdrop-blur-md supports-[backdrop-filter]:bg-white/75",
        className
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-6 px-4 sm:px-6 lg:px-8">
        <Brand to={brandTo} className="shrink-0" identity={brandIdentity} />

        <nav aria-label="Main" className="hidden flex-1 items-center gap-1 lg:flex">
          {nav.map((item) =>
            item.children ? (
              <NavDropdown key={item.label} item={item} active={isActive(item)} />
            ) : (
              <SmartLink
                key={item.label}
                href={item.href ?? "/"}
                className={cn(
                  "inline-flex h-9 items-center rounded-lg px-3 text-sm font-medium transition-colors",
                  isActive(item) ? "text-gray-900" : "text-gray-600 hover:text-gray-900"
                )}
                aria-current={isActive(item) ? "page" : undefined}
              >
                {item.label}
              </SmartLink>
            )
          )}
        </nav>

        <div className="ml-auto hidden items-center gap-2 lg:flex">
          {utility && <div className="hidden xl:block">{utility}</div>}
          {actions}
        </div>

        <button
          type="button"
          className="ml-auto inline-flex size-9 items-center justify-center rounded-lg text-gray-700 transition-colors hover:bg-gray-100 lg:hidden"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="site-mobile-menu"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div
          id="site-mobile-menu"
          className="max-h-[calc(100dvh-4rem)] animate-fade-in overflow-y-auto border-t border-gray-200 bg-white lg:hidden"
        >
          <nav aria-label="Mobile" className="mx-auto max-w-7xl space-y-1 px-4 py-4 sm:px-6">
            {[...nav, ...mobileExtraNav].map((item) =>
              item.children ? (
                <details key={item.label} className="group rounded-lg">
                  <summary className="flex h-11 cursor-pointer list-none items-center justify-between rounded-lg px-3 text-sm font-medium text-gray-900 hover:bg-gray-50 [&::-webkit-details-marker]:hidden">
                    {item.label}
                    <ChevronDown className="size-4 text-gray-400 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="mb-2 ml-3 border-l border-gray-200 pl-2">
                    {item.children.map((link) => (
                      <SmartLink
                        key={link.href + link.label}
                        href={link.href}
                        external={link.external}
                        onClick={() => setMobileOpen(false)}
                        className="flex h-10 items-center justify-between rounded-lg px-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      >
                        {link.label}
                        {link.external && <ArrowUpRight className="size-4 text-gray-300" />}
                      </SmartLink>
                    ))}
                  </div>
                </details>
              ) : (
                <SmartLink
                  key={item.label}
                  href={item.href ?? "/"}
                  onClick={() => setMobileOpen(false)}
                  className="flex h-11 items-center rounded-lg px-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  {item.label}
                </SmartLink>
              )
            )}
            {(mobileActions ?? actions) && (
              <div
                className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-4 [&>*]:w-full"
                // Close the drawer when an action runs (e.g. "Contact support" opening a modal),
                // but not for disclosure triggers such as a MenuButton that expands in place
                onClick={(e) => {
                  const el = (e.target as HTMLElement).closest("a, button");
                  if (el && !el.hasAttribute("aria-expanded")) setMobileOpen(false);
                }}
              >
                {mobileActions ?? actions}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
